import React from 'react';
import { Card, Col, Row, Skeleton } from 'antd';
import './DashboardSkeleton.css';

// Full-page dashboard loading skeleton. Mirrors mileslide's real dashboard
// layout: sidebar (logo, create, search, Quick Access + Folders sections),
// header, template gallery row and recent-document cards. Self-contained with
// scoped `dashboard-skeleton-*` classes so it never collides with the live
// Sidebar/Header/TemplateGallery styles.
const DashboardSkeleton = ({
    templateCount = 6,
    documentCount = 8,
}) => {
    const visibleTemplateCount = Math.min(templateCount, 6);
    const visibleDocumentCount = Math.min(documentCount, 8);
    const templates = Array.from({ length: visibleTemplateCount }, (_, index) => `template-${index}`);
    const templateChips = Array.from({ length: 10 }, (_, index) => `chip-${index}`);
    const documents = Array.from({ length: visibleDocumentCount }, (_, index) => `document-${index}`);
    const quickAccessItems = Array.from({ length: 2 }, (_, index) => ({
        key: `quick-${index}`,
        widthClass: 'dashboard-skeleton-sidebar-item-full',
    }));
    const folderItems = Array.from({ length: 6 }, (_, index) => ({
        key: `folder-${index}`,
        widthClass: 'dashboard-skeleton-sidebar-item-full',
    }));

    return (
        <div className="dashboard-skeleton-shell" aria-busy="true" aria-live="polite">
            <aside className="dashboard-skeleton-sidebar">
                <div className="dashboard-skeleton-sidebar-header">
                    <Skeleton.Input active className="dashboard-skeleton-sidebar-logo-line" size="small" />
                    <Skeleton.Avatar active shape="circle" size="small" className="dashboard-skeleton-sidebar-toggle" />
                </div>
                <div className="dashboard-skeleton-sidebar-actions">
                    <Skeleton.Button active block className="dashboard-skeleton-sidebar-create" />
                    <Skeleton.Input active block className="dashboard-skeleton-sidebar-search" size="small" />
                </div>
                <div className="dashboard-skeleton-sidebar-divider" />
                <div className="dashboard-skeleton-sidebar-nav">
                    <div className="dashboard-skeleton-sidebar-section-row">
                        <Skeleton.Input active className="dashboard-skeleton-sidebar-section-title" size="small" />
                        <Skeleton.Avatar active shape="square" size="small" className="dashboard-skeleton-sidebar-section-toggle" />
                    </div>
                    {quickAccessItems.map((item) => (
                        <div
                            key={item.key}
                            className="dashboard-skeleton-sidebar-item-row"
                        >
                            <Skeleton.Avatar active shape="square" size="small" className="dashboard-skeleton-sidebar-item-icon" />
                            <Skeleton.Input
                                active
                                block
                                className={`dashboard-skeleton-sidebar-item ${item.widthClass}`}
                                size="small"
                            />
                        </div>
                    ))}

                    <Skeleton.Button active block className="dashboard-skeleton-sidebar-create dashboard-skeleton-sidebar-create-folder" />

                    <div className="dashboard-skeleton-sidebar-section-row dashboard-skeleton-sidebar-folders-row">
                        <Skeleton.Input active className="dashboard-skeleton-sidebar-section-title" size="small" />
                        <Skeleton.Avatar active shape="square" size="small" className="dashboard-skeleton-sidebar-section-toggle" />
                    </div>
                    {folderItems.map((item) => (
                        <div key={item.key} className="dashboard-skeleton-sidebar-item-row">
                            <Skeleton.Avatar active shape="square" size="small" className="dashboard-skeleton-sidebar-folder-icon" />
                            <Skeleton.Input
                                active
                                block
                                className={`dashboard-skeleton-sidebar-item ${item.widthClass}`}
                                size="small"
                            />
                        </div>
                    ))}
                </div>
            </aside>

            <main className="dashboard-skeleton-main">
                <header className="dashboard-skeleton-header">
                    <div className="dashboard-skeleton-header-left">
                        <Skeleton.Avatar active shape="square" size="large" className="dashboard-skeleton-header-collapse" />
                        <Skeleton.Avatar active shape="square" size="large" className="dashboard-skeleton-header-brand-icon" />
                        <Skeleton.Input active className="dashboard-skeleton-header-brand-text" size="small" />
                    </div>
                    <div className="dashboard-skeleton-header-right">
                        <div className="dashboard-skeleton-header-sort-bar dashboard-skeleton-shimmer-block" />
                        <div className="dashboard-skeleton-header-view-toggle">
                            <div className="dashboard-skeleton-header-view-active dashboard-skeleton-shimmer-block" />
                            <div className="dashboard-skeleton-header-view-inactive dashboard-skeleton-shimmer-block" />
                        </div>
                        <Skeleton.Avatar active shape="square" size="large" className="dashboard-skeleton-header-apps" />
                        <Skeleton.Avatar active size="large" className="dashboard-skeleton-header-avatar" />
                    </div>
                </header>

                <div className="dashboard-skeleton">
                    <section className="dashboard-skeleton-section">
                        <div className="dashboard-skeleton-template-header">
                            <div className="dashboard-skeleton-template-header-left">
                                <div className="dashboard-skeleton-template-heading-bar dashboard-skeleton-shimmer-block" />
                            </div>
                            <div className="dashboard-skeleton-template-header-right">
                                <div className="dashboard-skeleton-template-link-bar dashboard-skeleton-shimmer-block" />
                                <div className="dashboard-skeleton-template-create-btn-bar dashboard-skeleton-shimmer-block" />
                            </div>
                        </div>

                        <div className="dashboard-skeleton-template-chips-row">
                            {templateChips.map((chip, index) => (
                                <div
                                    key={chip}
                                    className={`dashboard-skeleton-template-chip-bar dashboard-skeleton-shimmer-block ${index === 0 ? 'dashboard-skeleton-template-chip-bar-active' : ''}`}
                                />
                            ))}
                        </div>

                        <div className="dashboard-skeleton-template-scroll">
                            <div className="dashboard-skeleton-template-row">
                                {templates.map((item) => (
                                    <Card key={item} className="dashboard-skeleton-template-card">
                                        <div className="dashboard-skeleton-template-image">
                                            <Skeleton.Image active />
                                        </div>

                                        <div className="dashboard-skeleton-template-title-bar dashboard-skeleton-shimmer-block" />

                                        <div className="dashboard-skeleton-template-footer-row">
                                            <div className="dashboard-skeleton-template-usage-row">
                                                <div className="dashboard-skeleton-template-usage-icon dashboard-skeleton-shimmer-block" />
                                                <div className="dashboard-skeleton-template-usage-text dashboard-skeleton-shimmer-block" />
                                            </div>
                                            <div className="dashboard-skeleton-template-menu-vertical">
                                                <div className="dashboard-skeleton-template-menu-dot dashboard-skeleton-shimmer-block" />
                                                <div className="dashboard-skeleton-template-menu-dot dashboard-skeleton-shimmer-block" />
                                                <div className="dashboard-skeleton-template-menu-dot dashboard-skeleton-shimmer-block" />
                                            </div>
                                        </div>
                                    </Card>
                                ))}
                            </div>
                        </div>
                    </section>

                    <section className="dashboard-skeleton-section">
                        <div className="dashboard-skeleton-section-title dashboard-skeleton-recent-title-wrap">
                            <div className="dashboard-skeleton-recent-title-bar dashboard-skeleton-shimmer-block" />
                        </div>

                        <Row gutter={[16, 16]}>
                            {documents.map((item) => (
                                <Col key={item} xs={24} sm={12} lg={8} xl={6}>
                                    <Card className="dashboard-skeleton-doc-card">
                                        <div className="dashboard-skeleton-doc-stack">
                                            <div className="dashboard-skeleton-doc-title-row">
                                                <Skeleton.Input active className="dashboard-skeleton-doc-title" size="small" block />
                                                <Skeleton.Input active className="dashboard-skeleton-doc-edit" size="small" />
                                            </div>

                                            <div className="dashboard-skeleton-user-row">
                                                <Skeleton.Avatar active size="small" className="dashboard-skeleton-doc-author-avatar" />
                                                <Skeleton.Input active block className="dashboard-skeleton-user-text" size="small" />
                                            </div>

                                            <div className="dashboard-skeleton-doc-meta-block">
                                                {['meta-1', 'meta-2', 'meta-3'].map((rowKey) => (
                                                    <div key={rowKey} className="dashboard-skeleton-doc-meta-row">
                                                        <div className="dashboard-skeleton-doc-meta-key-bar" />
                                                        <div className="dashboard-skeleton-doc-meta-value-bar" />
                                                    </div>
                                                ))}
                                            </div>

                                            <div className="dashboard-skeleton-doc-spacer" />

                                            <div className="dashboard-skeleton-doc-footer">
                                                <div className="dashboard-skeleton-tag-row">
                                                    <Skeleton.Button active className="dashboard-skeleton-tag-btn" size="small" />
                                                    <Skeleton.Button active className="dashboard-skeleton-tag-btn-sm" size="small" />
                                                </div>
                                                <div className="dashboard-skeleton-doc-members">
                                                    <Skeleton.Avatar active size="small" />
                                                    <Skeleton.Avatar active size="small" />
                                                </div>
                                            </div>
                                        </div>
                                    </Card>
                                </Col>
                            ))}
                        </Row>
                    </section>
                </div>
            </main>
        </div>
    );
};

export default DashboardSkeleton;
